import { useState, useEffect } from "react";

// react-router components
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

import "./utils/axiosConfig"; // Import axios config

// Material Dashboard 2 React example components
import Sidenav from "./shared/Sidenav";
import Configurator from "./shared/Configurator";

// Material Dashboard 2 React themes
import theme from "./assets/theme";

// Material Dashboard 2 React Dark Mode themes
import themeDark from "./assets/theme-dark";

// Material Dashboard 2 React routes
import routes from "./routes";

// Material Dashboard 2 React contexts
import {
  useMaterialUIController,
  setMiniSidenav,
} from "./context";

// Auth Hook and Guards
import { useAuth } from "./hooks/useAuth";
import RoleGuard from "./guards/RoleGuard";
import GuestGuard from "./guards/GuestGuard";

// Images
import brandWhite from "./assets/images/logo-ct.png";
import brandDark from "./assets/images/logo-ct-dark.png";

export default function App() {
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const { pathname } = useLocation();

  const { user, isAuthenticated, isLoading } = useAuth();

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const getRoutes = (allRoutes) => {
    return allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        let element = route.component;

        if (route.key === "sign-in" || route.key === "sign-up" || route.key === "sign-out") {
          element = <GuestGuard>{route.component}</GuestGuard>;
        }
        else {
          // Default to RoleGuard for all other routes
          element = <RoleGuard roles={route.role ? [route.role, "admin"] : []}>{route.component}</RoleGuard>;
        }

        return (
          <Route
            exact
            path={route.route}
            element={element}
            key={route.key}
          />
        );
      }

      return null;
    });
  };

  // Filter routes for Sidenav to hide "sign-in", "sign-out" etc if needed? 
  // The Sidenav component usually renders what is passed.
  // We pass 'routes' which includes everything. 
  // Ideally we filter out things that shouldn't be in sidebar in Sidenav component or here.
  // But existing code passed 'routes' directly.
  // However, we added role property. Sidenav needs to be reinforced to check roles.

  if (isLoading) {
    return null; // Or a loading spinner
  }

  return (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />

      {isAuthenticated && layout === "dashboard" && (
        <>
          <Sidenav
            color={sidenavColor}
            brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
            brandName="RevistaGuineo"
            routes={routes.filter(r => !r.role || r.role === user?.role || user?.role === "admin")} // Basic client-side filtering for sidebar
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
          />
          <Configurator />
        </>
      )}

      {isAuthenticated && layout === "vr" && <Configurator />}

      <Routes>
        {getRoutes(routes)}
        <Route path="*" element={<Navigate to={isAuthenticated ? "/dashboard" : "/authentication/sign-in"} />} />
      </Routes>
    </ThemeProvider>
  );
}

